// RrulePicker — canonical `rrule-picker` field primitive (DRIFT-910 slice 3,
// CHG-2478). Ported from the v1 hand-rolled picker
// (web/dashboard/src/components/forms/RrulePicker.tsx, CHG-0680): the RFC-5545
// parse/build/humanize string logic is carried over 1:1 (pure, styling-free),
// but the UI is reimplemented against the v2 FieldPrimitiveProps contract +
// FieldShell chrome with CSS-var styling (no v1 Tailwind ag-* classes), NOT
// copied verbatim. String-emitting: storage is the RRULE string (cadence_rrule,
// String(255) server-side).
//
// Subset of RFC-5545 (mirrors v1): FREQ (DAILY|WEEKLY|MONTHLY|YEARLY), INTERVAL,
// BYDAY (weekly), BYMONTHDAY (monthly), UNTIL/COUNT (end). Out of scope:
// BYWEEKNO/BYYEARDAY/BYSETPOS/EXDATE.
//
// Contract: specs/_product/field-primitive-registry.md §1 ('rrule-picker').
// Doctrine: feedback_field_primitive_components_canonical.md. a11y (§2): the
// composite is wrapped in FieldShell so the label + error(role=alert) + helper
// render; aria-describedby is applied to the FREQ select (the primary control).

import { useEffect, useMemo, useRef, useState } from "react";
import type { FieldPrimitiveProps } from "./types";
import { FieldShell, fieldControlStyle, useFieldIds, describedBy } from "./FieldShell";

type Frequency = "DAILY" | "WEEKLY" | "MONTHLY" | "YEARLY";

const FREQ_OPTIONS: Array<{ value: Frequency; label: string }> = [
  { value: "DAILY", label: "Daily" },
  { value: "WEEKLY", label: "Weekly" },
  { value: "MONTHLY", label: "Monthly" },
  { value: "YEARLY", label: "Yearly" },
];

const WEEKDAYS: Array<{ code: string; label: string }> = [
  { code: "MO", label: "Mon" },
  { code: "TU", label: "Tue" },
  { code: "WE", label: "Wed" },
  { code: "TH", label: "Thu" },
  { code: "FR", label: "Fri" },
  { code: "SA", label: "Sat" },
  { code: "SU", label: "Sun" },
];

interface ParsedRrule {
  freq: Frequency;
  interval: number;
  byDay: string[];
  byMonthDay: number | null;
  end: { kind: "never" } | { kind: "until"; date: string } | { kind: "count"; n: number };
}

const DEFAULT_PARSED: ParsedRrule = {
  freq: "WEEKLY",
  interval: 1,
  byDay: [],
  byMonthDay: null,
  end: { kind: "never" },
};

// ── pure RFC-5545 string logic (ported 1:1 from v1 CHG-0680) ──────────────────
export function parseRrule(rrule: string | null | undefined): ParsedRrule {
  if (!rrule) return DEFAULT_PARSED;
  const trimmed = rrule.replace(/^RRULE:/i, "").trim();
  if (!trimmed) return DEFAULT_PARSED;
  const parts = new Map<string, string>();
  for (const seg of trimmed.split(";")) {
    const eq = seg.indexOf("=");
    if (eq <= 0) continue;
    parts.set(seg.slice(0, eq).toUpperCase(), seg.slice(eq + 1));
  }
  const freqRaw = (parts.get("FREQ") || "WEEKLY").toUpperCase();
  const freq: Frequency = FREQ_OPTIONS.find((o) => o.value === freqRaw)?.value || "WEEKLY";
  const interval = Math.max(1, parseInt(parts.get("INTERVAL") || "1", 10) || 1);
  const byDay = (parts.get("BYDAY") || "")
    .split(",")
    .map((s) => s.trim().toUpperCase())
    .filter((s) => WEEKDAYS.some((w) => w.code === s));
  const byMonthDayRaw = parts.get("BYMONTHDAY");
  const byMonthDay = byMonthDayRaw
    ? (() => {
        const n = parseInt(byMonthDayRaw, 10);
        if (Number.isNaN(n)) return null;
        if (n === -1) return -1;
        if (n >= 1 && n <= 31) return n;
        return null;
      })()
    : null;
  let end: ParsedRrule["end"] = { kind: "never" };
  const until = parts.get("UNTIL");
  const count = parts.get("COUNT");
  if (until) {
    const m = until.match(/^(\d{4})(\d{2})(\d{2})/);
    if (m) end = { kind: "until", date: `${m[1]}-${m[2]}-${m[3]}` };
  } else if (count) {
    const n = parseInt(count, 10);
    if (n >= 1) end = { kind: "count", n };
  }
  return { freq, interval, byDay, byMonthDay, end };
}

export function buildRrule(p: ParsedRrule): string {
  const parts: string[] = [`FREQ=${p.freq}`];
  if (p.interval > 1) parts.push(`INTERVAL=${p.interval}`);
  if (p.freq === "WEEKLY" && p.byDay.length > 0) parts.push(`BYDAY=${p.byDay.join(",")}`);
  if (p.freq === "MONTHLY" && p.byMonthDay != null) parts.push(`BYMONTHDAY=${p.byMonthDay}`);
  if (p.end.kind === "until") parts.push(`UNTIL=${p.end.date.replace(/-/g, "")}`);
  else if (p.end.kind === "count") parts.push(`COUNT=${p.end.n}`);
  return parts.join(";");
}

export function humanizeRrule(p: ParsedRrule): string {
  const intervalPrefix =
    p.interval === 1
      ? { DAILY: "day", WEEKLY: "week", MONTHLY: "month", YEARLY: "year" }[p.freq]
      : `${p.interval} ${{ DAILY: "days", WEEKLY: "weeks", MONTHLY: "months", YEARLY: "years" }[p.freq]}`;
  let phrase = `Every ${intervalPrefix}`;
  if (p.freq === "WEEKLY" && p.byDay.length > 0) {
    const labels = WEEKDAYS.filter((w) => p.byDay.includes(w.code)).map((w) => w.label);
    phrase += ` on ${labels.join(", ")}`;
  }
  if (p.freq === "MONTHLY" && p.byMonthDay != null) {
    phrase += p.byMonthDay === -1 ? " on the last day" : ` on day ${p.byMonthDay}`;
  }
  if (p.end.kind === "until") phrase += ` until ${p.end.date}`;
  else if (p.end.kind === "count") phrase += `, ${p.end.n} time${p.end.n === 1 ? "" : "s"}`;
  return phrase;
}

// ── v2 styling helpers (CSS-var; composition rule 5/9 clean) ──────────────────
const labelStyle = { fontSize: 11, fontWeight: 600, color: "var(--color-text-muted)" } as const;
const rowStyle = { display: "flex", flexDirection: "column", gap: 4 } as const;

export function RrulePicker({
  name,
  value,
  onChange,
  label,
  error,
  disabled,
  required,
  helperText,
}: FieldPrimitiveProps<string>) {
  const ids = useFieldIds();
  // Parse incoming RRULE once on mount (v1 CHG-1099 — never emit on mount; the
  // parent already holds the initial value).
  const initial = useMemo(() => parseRrule(value), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [state, setState] = useState<ParsedRrule>(initial);
  const userInteractedRef = useRef(false);
  const updateState = (next: ParsedRrule | ((s: ParsedRrule) => ParsedRrule)) => {
    userInteractedRef.current = true;
    setState(next);
  };

  // Mirror state → parent (only after a user edit; v1 CHG-1099).
  useEffect(() => {
    if (!userInteractedRef.current) return;
    onChange(buildRrule(state));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  // Re-sync when the parent passes a genuinely different value (v1 CHG-1099 pass-3).
  useEffect(() => {
    const incoming = (value || "").trim();
    if (incoming !== buildRrule(state)) {
      setState(parseRrule(value));
      userInteractedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <FieldShell
      htmlFor={ids.inputId}
      label={label}
      required={required}
      error={error}
      helperText={helperText}
      errorId={ids.errorId}
      helperId={ids.helperId}
    >
      <div
        data-rrule-picker={name}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12,
          padding: 12,
          borderRadius: "var(--radius-md)",
          border: error ? "1px solid var(--color-error)" : "1px solid var(--color-border)",
          background: "var(--color-bg-subtle, var(--color-surface))",
        }}
      >
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div style={rowStyle}>
            <span style={labelStyle}>Frequency</span>
            <select
              id={ids.inputId}
              name={name}
              value={state.freq}
              disabled={disabled}
              aria-invalid={error ? true : undefined}
              aria-required={required || undefined}
              aria-describedby={describedBy(ids, error, helperText)}
              onChange={(e) => updateState((s) => ({ ...s, freq: e.target.value as Frequency }))}
              style={fieldControlStyle(Boolean(error))}
            >
              {FREQ_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>Interval</span>
            <input
              type="number"
              aria-label="Repeat interval"
              min={1}
              max={99}
              value={state.interval}
              disabled={disabled}
              onChange={(e) =>
                updateState((s) => ({ ...s, interval: Math.max(1, parseInt(e.target.value, 10) || 1) }))
              }
              style={fieldControlStyle(false)}
            />
          </div>
        </div>

        {state.freq === "WEEKLY" && (
          <div style={rowStyle}>
            <span style={labelStyle}>On these days</span>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {WEEKDAYS.map((w) => {
                const active = state.byDay.includes(w.code);
                return (
                  <button
                    key={w.code}
                    type="button"
                    disabled={disabled}
                    aria-pressed={active}
                    onClick={() =>
                      updateState((s) => ({
                        ...s,
                        byDay: active ? s.byDay.filter((d) => d !== w.code) : [...s.byDay, w.code],
                      }))
                    }
                    style={{
                      padding: "4px 10px",
                      fontSize: 12,
                      borderRadius: "var(--radius-md)",
                      border: "1px solid var(--color-border)",
                      background: active ? "var(--color-primary)" : "var(--color-surface)",
                      color: active ? "var(--color-on-primary, #fff)" : "var(--color-text-muted)",
                      cursor: disabled ? "default" : "pointer",
                    }}
                  >
                    {w.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {state.freq === "MONTHLY" && (
          <div style={rowStyle}>
            <span style={labelStyle}>On day of month</span>
            <select
              aria-label="Day of month"
              value={state.byMonthDay == null ? "" : String(state.byMonthDay)}
              disabled={disabled}
              onChange={(e) =>
                updateState((s) => ({
                  ...s,
                  byMonthDay: e.target.value === "" ? null : parseInt(e.target.value, 10),
                }))
              }
              style={fieldControlStyle(false)}
            >
              <option value="">— any day (uses event start date) —</option>
              {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                <option key={d} value={d}>
                  Day {d}
                </option>
              ))}
              <option value="-1">Last day of month</option>
            </select>
          </div>
        )}

        <div style={rowStyle}>
          <span style={labelStyle}>Ends</span>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--color-text)" }}>
            <input
              type="radio"
              name={`${name}-end`}
              checked={state.end.kind === "never"}
              disabled={disabled}
              onChange={() => updateState((s) => ({ ...s, end: { kind: "never" } }))}
            />
            Never
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--color-text)" }}>
            <input
              type="radio"
              name={`${name}-end`}
              checked={state.end.kind === "until"}
              disabled={disabled}
              onChange={() =>
                updateState((s) => ({
                  ...s,
                  end: { kind: "until", date: s.end.kind === "until" ? s.end.date : "" },
                }))
              }
            />
            On date
            {/* composition-lint-exempt: canonical v2 rrule-picker primitive — the UNTIL end-date <input> IS part of the dispatch target (parallel to v1 components/forms/); rule 15's path-exemption omits v2 components/fields/ (gate-aim gap, flagged for cross-team re-aim) (DRIFT-910) */}
            <input
              type="date"
              aria-label="Repeat until date"
              value={state.end.kind === "until" ? state.end.date : ""}
              disabled={disabled || state.end.kind !== "until"}
              onChange={(e) => updateState((s) => ({ ...s, end: { kind: "until", date: e.target.value } }))}
              style={{ ...fieldControlStyle(false), width: "auto" }}
            />
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--color-text)" }}>
            <input
              type="radio"
              name={`${name}-end`}
              checked={state.end.kind === "count"}
              disabled={disabled}
              onChange={() =>
                updateState((s) => ({ ...s, end: { kind: "count", n: s.end.kind === "count" ? s.end.n : 10 } }))
              }
            />
            After
            <input
              type="number"
              aria-label="Number of occurrences"
              min={1}
              max={999}
              value={state.end.kind === "count" ? state.end.n : 10}
              disabled={disabled || state.end.kind !== "count"}
              onChange={(e) =>
                updateState((s) => ({
                  ...s,
                  end: { kind: "count", n: Math.max(1, parseInt(e.target.value, 10) || 1) },
                }))
              }
              style={{ ...fieldControlStyle(false), width: "5rem" }}
            />
            occurrences
          </label>
        </div>

        <div
          style={{
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--color-border)",
            background: "var(--color-surface)",
            padding: "6px 10px",
          }}
        >
          <div style={{ fontSize: 11, textTransform: "uppercase", color: "var(--color-text-muted)" }}>
            Cadence
          </div>
          <div style={{ fontSize: 13, color: "var(--color-text)" }}>{humanizeRrule(state)}</div>
          <div style={{ fontSize: 11, color: "var(--color-text-muted)", fontFamily: "monospace" }}>
            RRULE:{buildRrule(state)}
          </div>
        </div>
      </div>
    </FieldShell>
  );
}
